import { useRef, useState } from 'react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { AlertCircle, CheckCircle2, Loader2, Search, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { resultCount } from '@/lib/utils'
import { LoadingButton } from '@/components/loading-button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ManufacturersTable, ManufacturersTableSkeleton } from '@/components/manufacturers-table'
import { SearchSelect } from '@/components/search-select'
import { SUPPLIER_KIND_LABELS, useSuppliers } from '@/api/sourcing'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useImportManufacturerDiscounts,
  useImportPurchasingList,
  useManufacturers,
  useTaskStatus,
} from '@/api/hooks'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import type { UseMutationResult } from '@tanstack/react-query'
import { SearchInput } from '@/components/search-input'

const STATUS_OPTIONS = [
  { value: 'preferred_approved', label: 'Preferred & Approved' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'approved', label: 'Approved' },
  { value: 'unset', label: 'No Status' },
  { value: 'all', label: 'All statuses' },
]

const TRACKING_OPTIONS = [
  { value: 'all', label: 'All tracking' },
  { value: 'quoting', label: 'Quoting' },
  { value: 'storefront', label: 'Storefront' },
  { value: 'quoting_only', label: 'Quoting only' },
  { value: 'untracked', label: 'Not tracked' },
] as const

type TrackingFilter = (typeof TRACKING_OPTIONS)[number]['value']

// Who to ask for better terms, for accounting (USA-1535).
const TERMS_OPTIONS = [
  { value: 'all', label: 'All terms' },
  { value: 'prepaid', label: 'CIA / Prepaid' },
  { value: 'deposit', label: 'Deposit up front' },
  { value: 'due_on_receipt', label: 'Due on receipt' },
  { value: 'net', label: 'Net' },
  { value: 'discount_net', label: 'Early-pay discount' },
  { value: 'proximo', label: 'Net Prox' },
  { value: 'none', label: 'No terms on file' },
] as const

type TermsFilter = (typeof TERMS_OPTIONS)[number]['value']

const MANUFACTURER_SORTS = ['name', 'below_floor_products', 'below_map_products'] as const

/** Whose preferred source buys through a supplier (its UUID) or direct (USA-1535). */
const DIRECT = 'direct'

type ManufacturerSort = (typeof MANUFACTURER_SORTS)[number]

export function ManufacturersPage() {
  const [search, setSearch] = useUrlSearchText()
  const [{ status, tracking, page, sort: sortBy, source, terms }, setFilters] = useQueryStates({
    terms: parseAsStringLiteral(TERMS_OPTIONS.map((opt) => opt.value)).withDefault('all'),
    status: parseAsString.withDefault('all'),
    source: parseAsString,
    tracking: parseAsStringLiteral(TRACKING_OPTIONS.map((opt) => opt.value)).withDefault('all'),
    page: parseAsInteger.withDefault(1),
    sort: parseAsStringLiteral(MANUFACTURER_SORTS).withDefault('name'),
  })
  const [discountsOpen, setDiscountsOpen] = useState(false)
  const [purchasingOpen, setPurchasingOpen] = useState(false)
  const debouncedSearch = useDebouncedValue(search)
  const { data: suppliers } = useSuppliers()
  const sourceOptions = [
    { value: DIRECT, label: 'Direct', description: 'From the manufacturer' },
    ...(suppliers ?? []).map((supplier) => ({
      value: supplier.uuid,
      label: supplier.name,
      description: SUPPLIER_KIND_LABELS[supplier.kind],
    })),
  ]

  const { data, isLoading } = useManufacturers({
    search: debouncedSearch || undefined,
    status,
    tracking: tracking === 'all' ? undefined : tracking,
    source: source ?? undefined,
    terms: terms === 'all' ? undefined : terms,
    page,
    limit: 20,
    sort_by: sortBy,
    sort_dir: sortBy === 'name' ? 'asc' : 'desc',
  })

  // Filters set away from their defaults, counted on the Filters button.
  const activeFilters = [status !== 'all', tracking !== 'all', !!source, terms !== 'all', sortBy !== 'name'].filter(Boolean).length

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Manufacturers</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setDiscountsOpen(true)}>
            <Upload className="h-4 w-4" /> Import Discounts
          </Button>
          <Button variant="outline" onClick={() => setPurchasingOpen(true)}>
            <Upload className="h-4 w-4" /> Import Purchasing List
          </Button>
        </div>
      </div>

      <ListFilters

        count={resultCount(data?.total, 'manufacturer')}
        active={activeFilters}
        onClear={() => void setFilters({ terms: null, status: null, source: null, tracking: null, page: 1 })}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search by name, alias, product, or vendor..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                void setFilters({ page: 1 })
              }}
              className="pl-9"
            />
          </div>
        }
      >
        <ListFilter label="Status">
          <Select
            value={status}
            onValueChange={(v) => {
              void setFilters({ status: v, page: 1 })
            }}
          >
            <SelectTrigger aria-label="Manufacturer status filter" className="w-full">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="Tracking">
          <Select
            value={tracking}
            onValueChange={(v) => {
              void setFilters({ tracking: v as TrackingFilter, page: 1 })
            }}
          >
            <SelectTrigger aria-label="Tracking" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRACKING_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="Source">
          <SearchSelect
            value={source ?? undefined}
            options={sourceOptions}
            onChange={(value) => void setFilters({ source: value ?? null, page: 1 })}
            allLabel="All sources"
            searchPlaceholder="Search suppliers"
            ariaLabel="Source"
            className="h-9 w-full"
          />
        </ListFilter>
        <ListFilter label="Payment terms">
          <Select
            value={terms}
            onValueChange={(v) => {
              void setFilters({ terms: v as TermsFilter, page: 1 })
            }}
          >
            <SelectTrigger aria-label="Payment terms" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TERMS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="Sort by">
          <Select
            value={sortBy}
            onValueChange={(v) => {
              void setFilters({ sort: v as ManufacturerSort, page: 1 })
            }}
          >
            <SelectTrigger aria-label="Sort manufacturers" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="below_floor_products">Most below our floor</SelectItem>
              <SelectItem value="below_map_products">Most below MAP</SelectItem>
            </SelectContent>
          </Select>
        </ListFilter>
      </ListFilters>

      {isLoading ? (
        <ManufacturersTableSkeleton />
      ) : data?.items?.length ? (
        <ManufacturersTable items={data.items} />
      ) : (
        <div className="py-12 text-center text-muted-foreground">No manufacturers found</div>
      )}

      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: Math.max(1, page - 1) })}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: page + 1 })}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <ImportDialog
        open={discountsOpen}
        onOpenChange={setDiscountsOpen}
        title="Import Manufacturer Discounts"
        description="Upload the AQ Mfgr Export .xlsx file. The import runs as a background job — this dialog will poll until it finishes."
        useImportMutation={useImportManufacturerDiscounts}
      />
      <ImportDialog
        open={purchasingOpen}
        onOpenChange={setPurchasingOpen}
        title="Import Purchasing List"
        description="Upload the USA-RS Purchasing List .xlsx file. Uses AI to parse messy freetext fields — this can take a few minutes."
        useImportMutation={useImportPurchasingList}
      />
    </div>
  )
}

type UploadMutation = UseMutationResult<
  { task_uuid: string; task_name: string; filename: string },
  Error,
  File,
  unknown
>

interface ImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  useImportMutation: () => UploadMutation
}

function ImportDialog({
  open,
  onOpenChange,
  title,
  description,
  useImportMutation,
}: ImportDialogProps) {
  const mutation = useImportMutation()
  const [file, setFile] = useState<File | null>(null)
  const [taskUuid, setTaskUuid] = useState<string | null>(null)
  const [filename, setFilename] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: taskStatus } = useTaskStatus(taskUuid)
  const terminal = taskStatus?.status === 'completed' || taskStatus?.status === 'failed'

  function reset() {
    setFile(null)
    setTaskUuid(null)
    setFilename(null)
    mutation.reset()
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleClose(nextOpen: boolean) {
    if (!nextOpen) reset()
    onOpenChange(nextOpen)
  }

  function handleUpload() {
    if (!file) return
    mutation.mutate(file, {
      onSuccess: (res) => {
        setTaskUuid(res.task_uuid)
        setFilename(res.filename)
      },
    })
  }

  const uploading = mutation.isPending
  const inProgress = !!taskUuid && !terminal
  const uploadError = mutation.error?.message

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!taskUuid && (
          <div className="space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-accent"
            />
            {uploadError && (
              <p className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {uploadError}
              </p>
            )}
          </div>
        )}

        {taskUuid && (
          <div className="space-y-3 rounded-md border p-4">
            <div className="flex items-center gap-2 text-sm">
              {terminal ? (
                taskStatus?.status === 'completed' ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-destructive" />
                )
              ) : (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
              <span className="font-medium">
                {taskStatus?.status
                  ? taskStatus.status.replace('-', ' ').replace(/^./, (c) => c.toUpperCase())
                  : 'Queued'}
              </span>
              {filename && <span className="text-muted-foreground">— {filename}</span>}
            </div>
            {taskStatus?.entity_label && (
              <p className="text-sm text-muted-foreground">{taskStatus.entity_label}</p>
            )}
            {typeof taskStatus?.progress === 'number' && (
              <div className="space-y-1">
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${taskStatus.progress}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">{taskStatus.progress}%</p>
              </div>
            )}
            {taskStatus?.status === 'failed' && (
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-destructive/10 p-2 text-xs text-destructive">
                {taskStatus.error || 'Import failed. Check the worker logs for details.'}
              </pre>
            )}
            {taskStatus?.status === 'completed' && (
              <p className="text-sm text-green-700">Import finished successfully.</p>
            )}
          </div>
        )}

        <DialogFooter>
          {!taskUuid && (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <LoadingButton
                onClick={handleUpload}
                disabled={!file}
                loading={uploading}
                loadingText="Uploading..."
              >
                <Upload className="h-4 w-4" /> Upload
              </LoadingButton>
            </>
          )}
          {taskUuid && (
            <Button onClick={() => handleClose(false)} disabled={inProgress}>
              {terminal ? 'Close' : 'Running in background...'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
